import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Trophy, Crown, Medal, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Leaderboards() {
  const { user, refresh } = useAuth();
  const [scope, setScope] = useState("global");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get(`/leaderboards?scope=${scope}`).then(({ data }) => setRows(data));
  }, [scope]);

  const togglePublic = async () => {
    try {
      const next = !user.public_leaderboard;
      await api.post("/leaderboards/opt", { public_leaderboard: next });
      await refresh();
      toast.success(next ? "You're now on the leaderboard" : "You're hidden from leaderboards");
    } catch { toast.error("Couldn't update"); }
  };

  return (
    <div data-testid="leaderboards-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Leaderboards</h1>
          <p className="text-white/60 mt-1.5">Friendly competition. Privacy first.</p>
        </div>
        <button onClick={togglePublic} data-testid="toggle-public-leaderboard"
          className="btn-secondary-neon text-sm flex items-center gap-2">
          {user.public_leaderboard ? <Eye size={14}/> : <EyeOff size={14}/>}
          {user.public_leaderboard ? "On leaderboard" : "Hidden"}
        </button>
      </div>

      <div className="flex gap-2 mt-6">
        {["global", "organisation"].map(s => (
          <button key={s} onClick={() => setScope(s)} data-testid={`scope-${s}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition capitalize ${
              scope === s ? "bg-neonpink text-white shadow-glow" : "bg-white/5 text-white/70 border border-white/10"
            }`}>{s}</button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-8">
        {rows.slice(0, 3).map((r, i) => (
          <div key={r.user_id} className={`glass-card p-6 text-center ${i === 0 ? "border-neonyellow/30" : ""}`} data-testid={`top-${i + 1}`}>
            <div className="flex justify-center">
              {i === 0 ? <Crown className="text-neonyellow" size={28}/> :
               i === 1 ? <Medal className="text-white/70" size={26}/> :
                         <Medal className="text-neonyellow/70" size={26}/>}
            </div>
            <div className="w-16 h-16 rounded-full mx-auto mt-3 bg-gradient-to-br from-neonpink to-neoncyan flex items-center justify-center font-display text-xl font-black">
              {r.name[0].toUpperCase()}
            </div>
            <div className="font-display font-bold text-lg mt-2">{r.name}</div>
            <div className="text-sm text-white/60">Lvl {r.level} · {r.xp} XP</div>
          </div>
        ))}
      </div>

      <div className="glass-card mt-6 overflow-hidden">
        <table className="w-full">
          <thead className="text-xs uppercase tracking-widest text-white/50">
            <tr><th className="text-left p-4">#</th><th className="text-left p-4">Name</th><th className="text-right p-4">Level</th><th className="text-right p-4">XP</th><th className="text-right p-4">Streak</th></tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.user_id} className="border-t border-white/5 hover:bg-white/5" data-testid={`leaderboard-row-${r.user_id}`}>
                <td className="p-4 font-bold text-white/70">#{r.rank}</td>
                <td className="p-4 font-semibold">{r.name}{r.user_id === user.user_id ? <span className="ml-2 text-xs text-neoncyan">(you)</span> : null}</td>
                <td className="p-4 text-right">{r.level}</td>
                <td className="p-4 text-right text-neonyellow font-bold">{r.xp}</td>
                <td className="p-4 text-right">🔥 {r.streak}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="text-center p-8 text-white/50">No one to show yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
